/**
 * Stage IG-P1-A:Instagram データ取得 hook 群。
 *
 * 本プロジェクトは React Query / SWR を使わない(package.json に無い)。
 * 既存の useFolderCounts / useMailMessages と同じ
 * 「useState + useEffect + inFlightRef + refresh()」テンプレートに揃える。
 *
 * 返却形は全て { data, loading, error, errorCode, refresh } で統一。
 * ページ側は provider(mock / meta)を一切意識しない(設計書 §10)。
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { instagramApi } from "../../../api/instagram";
import { ApiError } from "../../../api/client";
import type {
  CapabilitiesResponse,
  CommentListResponse,
  ConnectionResponse,
  ConversationDetail,
  ConversationListResponse,
  DashboardResponse,
  DraftListResponse,
  MediaListResponse,
} from "../types";

export type AsyncState<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  refresh: () => void;
};

export type PaginatedAsyncState<T> = AsyncState<T> & {
  loadingMore: boolean;
  refreshingLatest: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refreshLatest: () => Promise<void>;
  replaceItem: (item: T extends PaginatedResponse<infer TItem> ? TItem : never) => void;
  reset: () => void;
};

function extractError(e: unknown): { message: string; code: string | null } {
  if (e instanceof ApiError) {
    return { message: e.message, code: e.code };
  }
  if (e instanceof Error) return { message: e.message, code: null };
  return { message: String(e), code: null };
}

/**
 * 共通の取得 hook。
 *
 * @param fetcher 取得関数。**必ず useCallback で安定化して渡すこと**
 *                (毎レンダー新しい関数を渡すと無限ループになる)
 */
function useAsyncResource<T>(
  fetcher: () => Promise<T>,
  enabled = true,
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    // 重要:React 18 StrictMode で useEffect は dev モードで
    // mount → unmount → mount する。inFlightRef.current = true した直後に
    // unmount が来ると、cancelled=true になってしまい、続く再 mount では
    // 2 度目の useEffect が `if (inFlightRef.current) return` で永遠にスキップされる
    // (loading=true が解除されない → button 永遠に灰色)。
    //
    // 修正:ref は使わず effect 内で完結させる。cancelled は cleanup 時に立て、
    // promise 解決時にだけ state を触る。これで React 18 dev での二重呼び出しでも
    // 1 度目は cancelled 後に何もしない、2 度目は新しいフェッチが走って正常 resolve、
    // 最終的に正しい state に収束する。
    let cancelled = false;
    setLoading(true);
    setError(null);
    setErrorCode(null);

    fetcher()
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const { message, code } = extractError(e);
        setError(message);
        setErrorCode(code);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetcher, enabled, reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  return { data, loading, error, errorCode, refresh };
}

type PaginatedResponse<TItem> = {
  items: TItem[];
  next_cursor: string | null;
};

function mergeLatestPage<TItem extends { id: string }>(prev: TItem[], latest: TItem[]): TItem[] {
  const latestIds = new Set(latest.map((item) => item.id));
  return [...latest, ...prev.filter((item) => !latestIds.has(item.id))];
}

function appendOlderPage<TItem extends { id: string }>(prev: TItem[], older: TItem[]): TItem[] {
  const existingIds = new Set(prev.map((item) => item.id));
  return [...prev, ...older.filter((item) => !existingIds.has(item.id))];
}

function prependOlderPage<TItem extends { id: string }>(prev: TItem[], older: TItem[]): TItem[] {
  const existingIds = new Set(prev.map((item) => item.id));
  return [...older.filter((item) => !existingIds.has(item.id)), ...prev];
}

function mergeLatestTailPage<TItem extends { id: string }>(prev: TItem[], latest: TItem[]): TItem[] {
  const latestIds = new Set(latest.map((item) => item.id));
  return [...prev.filter((item) => !latestIds.has(item.id)), ...latest];
}

function usePaginatedResource<
  TItem extends { id: string },
  TResponse extends PaginatedResponse<TItem>,
>(
  fetchPage: (cursor?: string | null) => Promise<TResponse>,
  enabled = true,
): PaginatedAsyncState<TResponse> {
  const [data, setData] = useState<TResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshingLatest, setRefreshingLatest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const loadingMoreRef = useRef(false);
  const refreshingLatestRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setErrorCode(null);

    fetchPage(null)
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const { message, code } = extractError(e);
        setError(message);
        setErrorCode(code);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, fetchPage, reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  const refreshLatest = useCallback(async () => {
    if (refreshingLatestRef.current) return;
    refreshingLatestRef.current = true;
    setRefreshingLatest(true);
    setError(null);
    setErrorCode(null);

    try {
      const result = await fetchPage(null);
      setData((prev) => {
        if (!prev) return result;
        return {
          ...result,
          items: mergeLatestPage(prev.items, result.items),
        };
      });
    } catch (e: unknown) {
      const { message, code } = extractError(e);
      setError(message);
      setErrorCode(code);
    } finally {
      refreshingLatestRef.current = false;
      setRefreshingLatest(false);
    }
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    const cursor = data?.next_cursor;
    if (!cursor || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setError(null);
    setErrorCode(null);

    void fetchPage(cursor)
      .then((result) => {
        setData((prev) => {
          if (!prev) return result;
          return {
            ...result,
            items: appendOlderPage(prev.items, result.items),
          };
        });
      })
      .catch((e: unknown) => {
        const { message, code } = extractError(e);
        setError(message);
        setErrorCode(code);
      })
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [data?.next_cursor, fetchPage]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setErrorCode(null);
  }, []);

  const replaceItem = useCallback((item: TItem) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((current) => (current.id === item.id ? item : current)),
      };
    });
  }, []);

  return {
    data,
    loading,
    error,
    errorCode,
    refresh,
    loadingMore,
    refreshingLatest,
    hasMore: Boolean(data?.next_cursor),
    loadMore,
    refreshLatest,
    replaceItem,
    reset,
  };
}

// ---------------------------------------------------------------------------
// capability(設計書 §13:画面起動時に取得)
// ---------------------------------------------------------------------------

export function useInstagramCapabilities(): AsyncState<CapabilitiesResponse> {
  const fetcher = useCallback(() => instagramApi.capabilities(), []);
  return useAsyncResource(fetcher);
}

// ---------------------------------------------------------------------------
// 接続状態
// ---------------------------------------------------------------------------

export function useInstagramConnection(): AsyncState<ConnectionResponse> {
  const fetcher = useCallback(() => instagramApi.connection(), []);
  return useAsyncResource(fetcher);
}

// ---------------------------------------------------------------------------
// ダッシュボード
// ---------------------------------------------------------------------------

export function useInstagramDashboard(): AsyncState<DashboardResponse> {
  const fetcher = useCallback(() => instagramApi.dashboard(), []);
  return useAsyncResource(fetcher);
}

// ---------------------------------------------------------------------------
// DM
// ---------------------------------------------------------------------------

export function useInstagramConversations(params: {
  status?: string;
  q?: string;
  limit?: number;
}): PaginatedAsyncState<ConversationListResponse> {
  const { status, q, limit } = params;
  const fetcher = useCallback(
    (cursor?: string | null) => instagramApi.listConversations({ status, q, limit, cursor }),
    [limit, q, status],
  );
  return usePaginatedResource(fetcher);
}

export function useInstagramConversation(
  conversationId: string | null,
): AsyncState<ConversationDetail> {
  const fetcher = useCallback(
    () =>
      conversationId
        ? instagramApi.getConversation(conversationId)
        : Promise.resolve(null as unknown as ConversationDetail),
    [conversationId],
  );
  return useAsyncResource(fetcher, Boolean(conversationId));
}

export type PaginatedConversationDetailState = AsyncState<ConversationDetail> & {
  loadingOlder: boolean;
  refreshingLatest: boolean;
  hasOlder: boolean;
  loadOlder: () => void;
  refreshLatest: () => Promise<void>;
};

export function useInstagramConversationWindow(
  conversationId: string | null,
  params: { limit?: number } = {},
): PaginatedConversationDetailState {
  const { limit = 10 } = params;
  const [data, setData] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [refreshingLatest, setRefreshingLatest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const activeConversationRef = useRef({ id: conversationId, generation: 0 });
  if (activeConversationRef.current.id !== conversationId) {
    activeConversationRef.current = {
      id: conversationId,
      generation: activeConversationRef.current.generation + 1,
    };
  }
  const loadingOlderRef = useRef<number | null>(null);
  const refreshingLatestRef = useRef<number | null>(null);

  const fetchPage = useCallback(
    (cursor?: string | null) => {
      if (!conversationId) {
        return Promise.resolve(null as unknown as ConversationDetail);
      }
      return instagramApi.getConversation(conversationId, { limit, cursor });
    },
    [conversationId, limit],
  );

  useEffect(() => {
    if (!conversationId) {
      setData(null);
      return;
    }
    let cancelled = false;
    const generation = activeConversationRef.current.generation;
    setLoading(true);
    setError(null);
    setErrorCode(null);

    fetchPage(null)
      .then((result) => {
        if (cancelled || activeConversationRef.current.generation !== generation || result.id !== conversationId) return;
        setData((prev) => activeConversationRef.current.generation === generation ? result : prev);
      })
      .catch((e: unknown) => {
        if (cancelled || activeConversationRef.current.generation !== generation) return;
        const { message, code } = extractError(e);
        setError(message);
        setErrorCode(code);
      })
      .finally(() => {
        if (cancelled || activeConversationRef.current.generation !== generation) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId, fetchPage, reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  const refreshLatest = useCallback(async () => {
    if (!conversationId || activeConversationRef.current.id !== conversationId) return;
    const generation = activeConversationRef.current.generation;
    if (refreshingLatestRef.current === generation) return;
    refreshingLatestRef.current = generation;
    setRefreshingLatest(true);
    setError(null);
    setErrorCode(null);

    try {
      const result = await fetchPage(null);
      if (activeConversationRef.current.generation !== generation || result.id !== conversationId) return;
      setData((prev) => {
        if (activeConversationRef.current.generation !== generation) return prev;
        if (!prev || prev.id !== conversationId) return result;
        return {
          ...prev,
          ...result,
          next_cursor: prev.messages.length > result.messages.length
            ? prev.next_cursor
            : result.next_cursor,
          messages: mergeLatestTailPage(prev.messages, result.messages),
        };
      });
    } catch (e: unknown) {
      if (activeConversationRef.current.generation !== generation) return;
      const { message, code } = extractError(e);
      setError(message);
      setErrorCode(code);
    } finally {
      if (refreshingLatestRef.current === generation) {
        refreshingLatestRef.current = null;
        setRefreshingLatest(false);
      }
    }
  }, [conversationId, fetchPage]);

  const loadOlder = useCallback(() => {
    const cursor = data?.id === conversationId ? data.next_cursor : null;
    if (!conversationId || !cursor || activeConversationRef.current.id !== conversationId) return;
    const generation = activeConversationRef.current.generation;
    if (loadingOlderRef.current === generation) return;
    loadingOlderRef.current = generation;
    setLoadingOlder(true);
    setError(null);
    setErrorCode(null);

    void fetchPage(cursor)
      .then((result) => {
        if (activeConversationRef.current.generation !== generation || result.id !== conversationId) return;
        setData((prev) => {
          if (activeConversationRef.current.generation !== generation) return prev;
          if (!prev || prev.id !== conversationId) return result;
          return {
            ...prev,
            ...result,
            messages: prependOlderPage(prev.messages, result.messages),
          };
        });
      })
      .catch((e: unknown) => {
        if (activeConversationRef.current.generation !== generation) return;
        const { message, code } = extractError(e);
        setError(message);
        setErrorCode(code);
      })
      .finally(() => {
        if (loadingOlderRef.current === generation) {
          loadingOlderRef.current = null;
          setLoadingOlder(false);
        }
      });
  }, [conversationId, data?.next_cursor, fetchPage]);

  return {
    data: data?.id === conversationId ? data : null,
    loading,
    error,
    errorCode,
    refresh,
    loadingOlder: loadingOlder && loadingOlderRef.current === activeConversationRef.current.generation,
    refreshingLatest: refreshingLatest && refreshingLatestRef.current === activeConversationRef.current.generation,
    hasOlder: Boolean(data?.id === conversationId && data.next_cursor),
    loadOlder,
    refreshLatest,
  };
}

// ---------------------------------------------------------------------------
// コメント / コンテンツ / 分析(P1-B で画面を作る。hook は先に用意しておく)
// ---------------------------------------------------------------------------

export function useInstagramComments(params: {
  status?: string;
  mediaId?: string;
  q?: string;
  limit?: number;
  enabled?: boolean;
}): PaginatedAsyncState<CommentListResponse> {
  const { status, mediaId, q, limit, enabled = true } = params;
  const fetcher = useCallback(
    (cursor?: string | null) => instagramApi.listComments({ status, mediaId, q, limit, cursor }),
    [limit, status, mediaId, q],
  );
  return usePaginatedResource(fetcher, enabled);
}

export function useInstagramMedia(params: {
  limit?: number;
} = {}): PaginatedAsyncState<MediaListResponse> {
  const { limit } = params;
  const fetcher = useCallback(
    (cursor?: string | null) => instagramApi.listMedia({ limit, cursor }),
    [limit],
  );
  return usePaginatedResource(fetcher);
}

export function useInstagramDrafts(
  status?: string,
): AsyncState<DraftListResponse> {
  const fetcher = useCallback(
    () => instagramApi.listDrafts(status),
    [status],
  );
  return useAsyncResource(fetcher);
}
